import { SimpleButton } from '@anm/components/stream/destination_management/AddDestinationButton';
import DestinationAccountList from '@anm/components/stream/destination_management/DestinationAccountList';
import { appNotificationActions } from '@anm/store/modules/appNotifications';
import { useCallback, useState, FC } from 'react';
import { useDispatch } from 'react-redux';

import { StreamDestinationStepProps } from '../../../types';
import Header from '../Header';

import Tabs from './Tabs';
import {
  AddDestinationWrapper,
  Content,
  DestinationsDescription,
  DestinationsWrapper,
  DividerLine,
  ErrorMessage,
  FooterWrapper,
  LimitWarning,
  SubmitButton
} from './Wrapper';
import { getDescription, getErrorsText, getSubmitButtonText, getTitleText, getWarnMessage } from './helpers';
import { useFilterDestinations } from './hooks';

const StreamDestinations: FC<StreamDestinationStepProps> = ({
  isEdit,
  userId,
  preview,
  isHosted,
  isPending,
  waveUrls,
  isWebinar,
  isScheduled,
  isUploading,
  selectedIds,
  isDuplicate,
  isStreamOwner,
  isWebinarEnabled,
  guestDestinationsIds,
  deleteGuestDestinations,
  isDestinationTabs,
  destinationsList,
  destinationErrors,
  isEditDestinations,
  isDisabledSubmitButton,
  isNoDestinationsWarning,
  isDestinationLimitMessage,
  onBack,
  onCreateStream,
  onWebinarChange,
  onEditDestination,
  onSelectDestination,
  renderAddDestination,
  onDeleteGuestDestination
}) => {
  const dispatch = useDispatch();

  const handleCreateStream = () => {
    onCreateStream(preview.src);
  };

  const errorText = getErrorsText(destinationErrors);
  const isDestinationError = destinationErrors.length > 0;

  const description = getDescription({ isScheduled, isHosted, isEditDestinations });

  const submitButtonText = getSubmitButtonText({
    isEdit,
    isHosted,
    isScheduled,
    isUploading,
    isDuplicate,
    isEditDestinations
  });

  const title = getTitleText({ isEdit, isDuplicate, isEditDestinations });

  const [activeTab, setActiveTab] = useState(0);

  const isOwnDestinations = activeTab === 0;

  const [destinations, renderConnectToFacebookModal] = useFilterDestinations({
    userId,
    waveUrls,
    isOwnDestinations,
    destinationsList
  });

  const showDestinationWarning = useCallback(() => {
    dispatch(
      appNotificationActions.notify({
        type: 'warning',
        description: `This destination is already in ${isDestinationTabs ? 'guests' : 'host'} destinations list`
      })
    );
  }, []);

  const checkedSelectedIds = isOwnDestinations
    ? selectedIds.filter(id => !guestDestinationsIds?.filter(g => !deleteGuestDestinations?.includes(g))?.includes(id))
    : selectedIds.filter(id => !deleteGuestDestinations?.includes(id));

  const handelSelectDestination = (id: string) =>
    guestDestinationsIds?.filter(d => !deleteGuestDestinations?.includes(d))?.includes(id) && isOwnDestinations
      ? showDestinationWarning()
      : onSelectDestination(id);

  const canShowWebinar = !isHosted && isStreamOwner && isOwnDestinations;

  return (
    <>
      {renderConnectToFacebookModal()}
      <Header title={title} onBack={onBack} />
      {isDestinationTabs && (
        <Tabs tabs={['My destinations', 'Guests’ destinations']} activeTab={activeTab} onChangeTab={setActiveTab} />
      )}
      <DividerLine />
      <Content>
        {isOwnDestinations && (
          <AddDestinationWrapper>
            {renderAddDestination ? renderAddDestination() : <SimpleButton buttonText="Add destination" />}
          </AddDestinationWrapper>
        )}
        {(isDestinationLimitMessage || isNoDestinationsWarning) && (
          <LimitWarning variant="warning" position="left">
            {getWarnMessage(isNoDestinationsWarning, isStreamOwner)}
          </LimitWarning>
        )}
        <DestinationsWrapper>
          <DestinationAccountList
            list={destinations}
            isEdit={!!isEdit}
            errors={destinationErrors}
            isScheduled={isScheduled}
            selectedIds={checkedSelectedIds}
            showToggles={isOwnDestinations ? true : !isEdit && !isEditDestinations}
            emptyListText={isOwnDestinations ? '' : 'You’ll see your guests’ destinations here'}
            onEdit={isOwnDestinations ? onEditDestination : undefined}
            onSelectDestination={handelSelectDestination}
            onDelete={isOwnDestinations ? undefined : onDeleteGuestDestination}
            {...{ isWebinar, onWebinarChange, canShowWebinar, isWebinarEnabled }}
          />
        </DestinationsWrapper>
      </Content>
      <DividerLine />
      <FooterWrapper>
        {isDestinationError && (
          <ErrorMessage variant="danger" position="left">
            <div dangerouslySetInnerHTML={{ __html: errorText }} />
          </ErrorMessage>
        )}
        {description && <DestinationsDescription>{description}</DestinationsDescription>}
        <SubmitButton
          size="large"
          variant="light-blue"
          isUploading={isUploading}
          isPending={isPending || isUploading}
          disabled={isDisabledSubmitButton || isDestinationError}
          onClick={handleCreateStream}
        >
          {submitButtonText}
        </SubmitButton>
      </FooterWrapper>
    </>
  );
};

export default StreamDestinations;
